import { Button, Cell, Grid, HFlow, InfoLabel, Modal, ModalBody, ModalFooter, ModalHeader, Tooltip } from 'bold-ui'
import { useAlert } from 'components/alert'
import { CheckboxField, createDecorator, NumberField, resolveValue, SelectField, SubmitButton } from 'components/form'
import { Form, FormRenderProps } from 'components/form/core/Form/Form'
import { CidSelectField } from 'components/form/field/select/CidSelectField/CidSelectField'
import { css } from 'emotion'
import { TamanhoFraldaGeriatricaEnum, TipoAtestadoEnum } from 'graphql/types.generated'
import { Fragment, useMemo, useState } from 'react'
import { metaPath } from 'util/metaPath'

import { AtestadoAssinaturaDigitalView } from '../assinatura-digital/AtestadoAssinaturaDigitalView'
import { AtestadoConfirmarAssinaturaDigitalModal } from '../assinatura-digital/AtestadoConfirmarAssinaturaDigitalModal'
import { MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED } from '../assinatura-digital/model-atestadoAssinaturaDigital'
import { useAtestadoAssinaturaDigitalForm } from '../assinatura-digital/useAtestadoAssinaturaDigitalForm'
import { AtestadoContentBox } from '../components/AtestadoContentBox'
import AtestadoModel, { CidadaoAtestado } from '../components/model-atestado'
import { useAtestadoCancelarEdicao } from '../hooks/useAtestadoCancelarEdicao'
import { tituloAtestado } from '../utils-atestado'
import { createSolicitacaoFraldasGeriatricasCalculations } from './calculator-solicitacaoFraldasGeriatricas'
import { tamanhosFralda } from './model-solicitacaoFraldasGeriatricas'
import { parseUnidadesPorDia } from './utils-solicitacaoFraldasGeriatricas'
import { solicitacaoFraldasGeriatricasFormValidator } from './validator-solicitacaoFraldasGeriatricas'

const path = metaPath<AtestadoModel>()

export interface SolicitacaoFraldasGeriatricasFormModalProps {
  onSubmit(values: AtestadoModel): void
  handleModalClose(): void
  cidadao: CidadaoAtestado
  initialValues: AtestadoModel
  atendimentoId: ID
  prontuarioId: ID
  dataAtendimento: Instant
}

export default function SolicitacaoFraldasGeriatricasFormModal(
  props: Readonly<SolicitacaoFraldasGeriatricasFormModalProps>
) {
  const { onSubmit, handleModalClose, cidadao, initialValues, atendimentoId, prontuarioId, dataAtendimento } = props

  const decorator = useMemo(() => createDecorator(...createSolicitacaoFraldasGeriatricasCalculations(path, cidadao)), [
    cidadao,
  ])

  const possuiCpfCns = !!(cidadao.cpf || cidadao.cns)
  const alert = useAlert()
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [isEmailCidadaoDirty, setIsEmailCidadaoDirty] = useState(false)

  const tipoAtestado = TipoAtestadoEnum.SOLICITACAO_FRALDAS_GERIATRICAS

  const handleSubmit = (values: AtestadoModel) => {
    onSubmit(values)
    handleModalClose()
    alert('success', 'Solicitação de fraldas geriátricas salva com sucesso')
  }

  const {
    emailCidadao,
    handleSubmitWrapper,
    isAssinando,
    isModalOpen,
    onConfirm,
    onCancel,
    errorMessage,
    onAlertClose,
  } = useAtestadoAssinaturaDigitalForm({
    cidadaoId: cidadao.id,
    atendimentoId,
    prontuarioId,
    tipoAtestado,
    dataAtendimento,
    onSubmit: handleSubmit,
  })

  const handleCancel = useAtestadoCancelarEdicao({
    handleModalClose,
    successMessage: 'Edição de solicitação de fraldas geriátricas cancelada com sucesso.',
  })

  const renderForm = (formProps: FormRenderProps<AtestadoModel>) => {
    const hasChanges = formProps.dirty || isEmailCidadaoDirty

    const values = resolveValue(formProps.values, path) || ({} as AtestadoModel)
    const totalUnidadesValue = values.unidadesPorDia ? values.unidadesPorDia * 30 : '-'

    return (
      <Fragment>
        <Modal open onClose={() => handleCancel(hasChanges)}>
          <ModalHeader title='Editar solicitação de fraldas geriátricas' />
          <ModalBody>
            <Grid gap={1}>
              {!possuiCpfCns && (
                <Cell size={12}>
                  <Grid gap={1}>
                    <Cell size={4}>
                      <NumberField
                        name={path.cpfCns}
                        label='CPF/CNS'
                        disabled={values.naoPossui}
                        required={!values.naoPossui}
                        maxLength={15}
                      />
                    </Cell>
                    <Cell>
                      <HFlow style={styles.naoPossuiContainer}>
                        <CheckboxField name={path.naoPossui} label='Não possui' />
                      </HFlow>
                    </Cell>
                  </Grid>
                </Cell>
              )}

              <Cell size={3}>
                <SelectField<TamanhoFraldaGeriatricaEnum>
                  name={path.tamanhoFralda}
                  label='Tamanho da fralda'
                  items={tamanhosFralda}
                  itemToString={(item) => item}
                  required
                />
              </Cell>

              <Cell size={3}>
                <NumberField
                  name={path.unidadesPorDia}
                  label='Recomendação diária'
                  maxLength={3}
                  required
                  parse={parseUnidadesPorDia}
                />
              </Cell>

              <Cell size={2}>
                <Tooltip text='Recomendação diária informada multiplicada por 30 dias ao mês'>
                  <span className={styles.tooltipSpan}>
                    <InfoLabel title='Total de unidades' childStyles={styles.totalUnidadesInfoLabelChild}>
                      {totalUnidadesValue}
                    </InfoLabel>
                  </span>
                </Tooltip>
              </Cell>

              <Cell size={4}>
                <CidSelectField name={path.cid10} label='CID10' renderGrupoCiapRelacionada={false} />
              </Cell>

              <AtestadoAssinaturaDigitalView
                name={path.assinadoDigitalmente}
                cidadaoId={cidadao.id}
                cidadaoEmail={emailCidadao}
                onEmailCidadaoChange={setIsSubmitDisabled}
                onDirtyChange={setIsEmailCidadaoDirty}
                hasCid10={!!formProps.values.cid10}
                style={styles.assinaturaDigital}
              />

              <Cell size={12}>
                <AtestadoContentBox title={tituloAtestado(tipoAtestado)} content={values.descricao} variant='modal' />
              </Cell>
            </Grid>
          </ModalBody>

          <ModalFooter>
            <HFlow justifyContent='flex-end'>
              <Button onClick={() => handleCancel(hasChanges)}>Cancelar</Button>
              <Tooltip text={isSubmitDisabled ? MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED : undefined}>
                <SubmitButton
                  kind='primary'
                  handleSubmit={formProps.handleSubmit}
                  disabled={isSubmitDisabled || isAssinando}
                  loading={isAssinando}
                >
                  Salvar
                </SubmitButton>
              </Tooltip>
            </HFlow>
          </ModalFooter>
        </Modal>

        {isModalOpen && (
          <AtestadoConfirmarAssinaturaDigitalModal
            emailCidadao={emailCidadao}
            onConfirm={onConfirm}
            onCancel={onCancel}
            isAssinando={isAssinando}
            errorMessage={errorMessage}
            onAlertClose={onAlertClose}
            tipoAtestado={tipoAtestado}
            descricao={formProps.values.descricao}
          />
        )}
      </Fragment>
    )
  }

  return (
    <Form<AtestadoModel>
      initialValues={initialValues}
      render={renderForm}
      onSubmit={handleSubmitWrapper}
      validate={solicitacaoFraldasGeriatricasFormValidator(possuiCpfCns)}
      decorators={[decorator]}
      suppressNotificationError
    />
  )
}

const styles = {
  naoPossuiContainer: css`
    margin-top: 1.75rem;
  `,
  assinaturaDigital: css`
    padding-left: 1rem;
  `,
  totalUnidadesInfoLabelChild: css`
    font-size: 1rem;
    padding-top: 0.5rem;
  `,
  tooltipSpan: css`
    display: inline-block;
  `,
}
